import { BaseCampaignPostItemDTO } from '@retail/campaign/types';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import {
  CampaignTenantType,
  useAddCampaignItems,
  useValidateCampaignItems,
} from '@retail/campaign/data-access';
import { useToast } from '@retail/hooks';
import { useCallback, useMemo } from 'react';
import { useInvalidateCampaignQueries } from './useInvalidateCampaignQueries';

interface Props {
  campaignId: string;
  type: CampaignTenantType;
}

export const useAddCampaignItemsActions = <T extends BaseCampaignPostItemDTO>({
  campaignId,
  type,
}: Props) => {
  const toast = useToast();
  const t = useCampaignTFunction();

  const { mutateAsync: mutateAddCampaignItems, isPending: isLoadingAddToCampaign } =
    useAddCampaignItems({ campaignId, type });

  const { mutateAsync: mutateValidateCampaignItems } = useValidateCampaignItems<T>({ type });

  const invalidateCampaignQueries = useInvalidateCampaignQueries({ campaignId });

  const validateItems = useCallback(
    async (campaignItems: T[]) => {
      try {
        const { validItems, invalidItems } = await mutateValidateCampaignItems({
          body: campaignItems,
        });

        return { validItems, invalidItems };
      } catch (error) {
        toast.error(t('campaign.error.genericError') + ': ' + error);
        return { validItems: null, invalidItems: null };
      }
    },
    [mutateValidateCampaignItems, t, toast]
  );

  const handleAddItems = useCallback(
    async (campaignItems: T[]) => {
      try {
        const { validItems, invalidItems } = await validateItems(campaignItems);
        if (validItems?.length && validItems.length > 0) {
          await mutateAddCampaignItems({ body: validItems });
          toast.success(
            validItems.length === 1
              ? t('campaign.messages.itemAdded', { count: validItems.length })
              : t('campaign.messages.itemsAdded', { count: validItems.length })
          );
        }

        if (invalidItems?.length && invalidItems.length > 0) {
          toast.error(
            invalidItems.length === 1
              ? t('campaign.messages.invalidatedItem', { count: invalidItems.length })
              : t('campaign.messages.invalidatedItems', { count: invalidItems.length })
          );
        }
        await invalidateCampaignQueries();
        return { validItems, invalidItems };
      } catch (error) {
        console.error(error);
        toast.error(t('campaign.error.genericError'));
        return { validItems: null, invalidItems: null };
      }
    },
    [invalidateCampaignQueries, mutateAddCampaignItems, t, toast, validateItems]
  );

  return useMemo(
    () => ({
      isLoadingAddToCampaign,
      handleAddItems,
      mutateAddCampaignItems,
    }),
    [isLoadingAddToCampaign, handleAddItems, mutateAddCampaignItems]
  );
};
